
import { PushNotifications } from '@capacitor/push-notifications';
import { getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, getDoc, updateDoc, deleteDoc, getDocs, collection } from 'firebase/firestore';
import { db, messaging } from '../firebaseConfig';
import { getPlatformType, isNotificationSupported } from '../utils/platformUtils';
import { getDeviceId } from '../utils/deviceUtils';

export default class NotificationService {
  private static instance: NotificationService;
  private platformType: string;
  private messaging: any;
  private currentUserId: string | null = null;
  private currentUserName: string | null = null;
  private currentOrganizationId: string | null = null;
  private onTokenReceived: ((token: string) => void) | null = null;
  private currentToken: string | null = null;
  private currentDeviceId: string | null = null;
  private isInitialized: boolean = false;
  private listenersAdded: boolean = false;
  private onNotificationReceived: ((notification: any) => void) | null = null;
  private webMessageListenerAdded: boolean = false;

  private constructor() {
    this.platformType = getPlatformType();
    // Don't generate device ID in constructor - wait until we have a user
    this.currentDeviceId = null;
  }

  public static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // Add a method to set the callback
  setOnTokenReceived(callback: (token: string) => void) {
    this.onTokenReceived = callback;
  }

  // Add a method to set the notification callback
  setOnNotificationReceived(callback: (notification: any) => void) {
    this.onNotificationReceived = callback;
  }

  // Set the current user ID and name for token storage
  setCurrentUser(userId: string, userName?: string) {
    this.currentUserId = userId;
    this.currentUserName = userName || null;
    // Generate device ID when user is set
    this.currentDeviceId = getDeviceId();
  }

  // Set the current organization ID for organization-scoped storage
  setCurrentOrganization(organizationId: string) {
    this.currentOrganizationId = organizationId;
  }

  // Get the current token
  getCurrentToken(): string | null {
    return this.currentToken;
  }

  // Get the current device ID
  getCurrentDeviceId(): string | null {
    return this.currentDeviceId;
  }

  // Clear the current token
  clearCurrentToken() {
    this.currentToken = null;
  }

  // Check if the service is already initialized
  isServiceInitialized(): boolean {
    return this.isInitialized;
  }

  // Reset initialization state (for logout)
  resetInitialization() {
    this.isInitialized = false;
    this.currentUserId = null;
    this.currentUserName = null;
    this.currentOrganizationId = null;
    this.currentToken = null;
    this.currentDeviceId = getDeviceId(); // Reset to current device ID
    this.listenersAdded = false;
    this.onNotificationReceived = null;
    this.webMessageListenerAdded = false;
  }

  // Clean up all listeners to prevent memory leaks
  cleanup() {
    try {
      if (this.platformType === 'ios' || this.platformType === 'android') {
        // Remove all Capacitor listeners
        PushNotifications.removeAllListeners();
      }
      
      // Reset all state
      this.resetInitialization();
    } catch (error) {
      // Error during cleanup - handled silently
    }
  }

  async initialize() {
    // Prevent duplicate initialization
    if (this.isInitialized) {
      return;
    }
    
    // Add a small delay to ensure DOM is fully loaded for PWA detection
    if (this.platformType === 'web') {
      await new Promise(resolve => setTimeout(resolve, 100));
      // Re-check platform type after delay
      this.platformType = getPlatformType();
    }
    
    // Check if notifications are supported in this environment
    const isSupported = isNotificationSupported();
    
    if (!isSupported) {
      return;
    }
    
    if (this.platformType === 'ios' || this.platformType === 'android') {
      await this.initializeNativeNotifications();
    } else if (this.platformType === 'web' || this.platformType === 'ios-pwa' || this.platformType === 'android-pwa') {
      await this.initializeWebNotifications();
    }
    
    // Mark as initialized
    this.isInitialized = true;
  }

  private async initializeNativeNotifications() {
    try {
      
      // Check current permissions first
      const currentPermissions = await PushNotifications.checkPermissions();

      // Request permission if not already granted
      if (currentPermissions.receive !== 'granted') {
        const permissionStatus = await PushNotifications.requestPermissions();
        
        if (permissionStatus.receive !== 'granted') {
          return;
        }
      }

      // Add listeners BEFORE registering to ensure we catch the token event
      // This prevents race conditions where the token event fires before listeners are attached
      if (!this.listenersAdded) {
        // Handle registration success
        PushNotifications.addListener('registration', (token) => {
          const registrationToken = token?.value;
          
          if (!registrationToken) {
            return;
          }
          
          // Skip storing raw APNs device tokens (hex strings).
          // Firebase Admin can only send using FCM registration tokens.
          if (this.isLikelyApnsToken(registrationToken)) {
            console.warn('[NotificationService] Received APNs device token, waiting for FCM token from Firebase Messaging.');
            return;
          }
          
          this.currentToken = registrationToken; // Track the current token
          // Store token in Firestore with device ID
          this.storeTokenInFirestore(registrationToken);
          // ** Call the callback **
          if (this.onTokenReceived) {
            this.onTokenReceived(registrationToken);
          }
        });

        // Handle registration error
        PushNotifications.addListener('registrationError', (error) => {
          // Error handled silently - user will be notified through UI if needed
        });

        // Handle push notifications when app is in foreground
        PushNotifications.addListener('pushNotificationReceived', (notification) => {
          // For native platforms, the system handles notification display
          // Only update React state for UI updates
          if (this.onNotificationReceived) {
            this.onNotificationReceived(notification);
          }
        });

        // Handle push notification click
        PushNotifications.addListener('pushNotificationActionPerformed', (notification) => {
          const notificationData = notification.notification.data || {};
          // Ensure type is set from notificationType if type is missing
          if (!notificationData.type && notificationData.notificationType) {
            notificationData.type = notificationData.notificationType;
          }
          this.handleNotificationClick(notificationData);
        });

        this.listenersAdded = true;
      }

      // Register with FCM/APNS (after listeners are attached)
      try {
        await PushNotifications.register();
      } catch (registerError) {
        throw registerError;
      }
    } catch (error) {
      // Error handled silently - will be caught by error boundary if critical
    }
  }

  private async initializeWebNotifications() {
    if (!('Notification' in window)) {
      return;
    }

    try {
      // Initialize messaging for web platform and PWA
      if (!this.messaging && (this.platformType === 'web' || this.platformType === 'ios-pwa' || this.platformType === 'android-pwa')) {
        this.messaging = messaging;
      }

      if (!this.messaging) {
        return;
      }

      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        // Explicitly register the Firebase messaging service worker
        // Note: Service worker file is in /public/ and is copied to build root
        // So the path '/firebase-messaging-sw.js' should work correctly
        if ('serviceWorker' in navigator) {
          try {
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            // Service worker registered successfully
            // Note: Message listener is added below in the guarded block to prevent duplicates
          } catch (error) {
            // Don't throw - allow app to continue without service worker
          }
        }

        const token = await getToken(this.messaging, {
          vapidKey: process.env.REACT_APP_VAPID_KEY
        });
        
        if (token) {
          this.currentToken = token; // Track the current token
          await this.storeTokenInFirestore(token);
          // ** Call the callback **
          if (this.onTokenReceived) {
            this.onTokenReceived(token);
          }
        }

        // Add onMessage listener for foreground notifications (only once)
        if (!this.webMessageListenerAdded) {
          onMessage(this.messaging, (payload) => {
            // Don't show notification here - let service worker handle all notifications
            // Only update React state for UI updates
            if (this.onNotificationReceived) {
              this.onNotificationReceived(payload);
            }
          });

          // Listen for messages from service worker (for deep linking)
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.addEventListener('message', (event) => {
              if (event.data && event.data.type === 'NOTIFICATION_CLICK') {
                // Handle deep link from service worker notification click
                this.handleNotificationClick(event.data.data);
              }
            });
          }
          
          this.webMessageListenerAdded = true;
        }
      }
    } catch (error) {
      // Error initializing web notifications - handled silently
    }
  }

  private async storeTokenInFirestore(token: string) {
    // Phase 2: Verify permission before storing token
    // This is a safety check to ensure we don't store tokens without proper permission
    try {
      const currentPermissions = await PushNotifications.checkPermissions();
      
      if (currentPermissions.receive !== 'granted') {
        return;
      }
    } catch (permissionCheckError) {
      // Continue with storage attempt even if permission check fails
      // This ensures we don't break existing functionality if permission check has issues
    }

    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        if (!this.currentUserId || !this.currentOrganizationId || !this.currentDeviceId) {
          return;
        }
        
        const deviceData = {
          fcmToken: token,
          platform: this.platformType,
          lastSeen: new Date(),
          appVersion: process.env.REACT_APP_VERSION || '1.0.1',
          updatedAt: new Date(),
          userName: this.currentUserName || 'Unknown User'
        };

        // Use organization-scoped device storage
        const deviceRef = doc(db, `organizations/${this.currentOrganizationId}/users/${this.currentUserId}/devices`, this.currentDeviceId);

        // Check if device already exists
        const deviceDoc = await getDoc(deviceRef);

        if (deviceDoc.exists()) {
          // Device exists, update the token and lastSeen
          await updateDoc(deviceRef, {
            ...deviceData,
            updatedAt: new Date()
          });
        } else {
          // Device doesn't exist, create new device record
          await setDoc(deviceRef, {
            ...deviceData,
            createdAt: new Date()
          });
        }
        
        // If we reach here, the operation was successful
        return;
        
      } catch (error) {
        retryCount++;
        
        if (retryCount >= maxRetries) {
          throw new Error(`Failed to store notification token after ${maxRetries} attempts: ${error.message}`);
        }
        
        // Wait before retry (exponential backoff)
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  private handleNotificationClick(data: any) {
    if (!data) {
      // Fallback to orders if no data
      this.navigateToOrders();
      return;
    }

    // Use navigationUtils for React Router navigation
    try {
      const { handleNotificationDeepLink } = require('../utils/navigationUtils');
      handleNotificationDeepLink(data);
    } catch (error) {
      // Fallback to window.location if navigationUtils fails
      this.fallbackNavigation(data);
    }
  }

  // Fallback navigation using window.location (used if React Router is not available)
  private fallbackNavigation(data: any) {
    if (!data) {
      window.location.href = '/orders';
      return;
    }

    switch (data.type) {
      case 'order_update':
      case 'ORDER_PLACED':
      case 'ORDER_APPROVED':
      case 'ORDER_READY_TO_DISPATCH':
      case 'ORDER_DISPATCHED':
        if (data.orderId) {
          window.location.href = `/orders?orderId=${encodeURIComponent(data.orderId)}`;
        } else {
          window.location.href = '/orders';
        }
        break;
      case 'PAYMENT_DUE_REMINDER':
        window.location.href = '/receivables';
        break;
      case 'user_notification':
        window.location.href = '/profile';
        break;
      case 'approval':
        if (data.entityType === 'organization' || data.entityType === 'user') {
          window.location.href = '/orders';
        } else {
          window.location.href = '/orders';
        }
        break;
      default:
        window.location.href = '/dashboard';
    }
  }

  // Helper method for navigating to orders page
  private navigateToOrders(orderId: string | null = null) {
    try {
      const { navigateToOrders } = require('../utils/navigationUtils');
      navigateToOrders(orderId);
    } catch (error) {
      if (orderId) {
        window.location.href = `/orders?orderId=${encodeURIComponent(orderId)}`;
      } else {
        window.location.href = '/orders';
      }
    }
  }

  async showNotification(title: string, body: string, data?: any) {
    if (this.platformType === 'ios' || this.platformType === 'android') {
      // For native platforms, notifications are handled by the system
    } else if (this.platformType === 'web' || this.platformType === 'ios-pwa' || this.platformType === 'android-pwa') {
      // For web platform and PWA
      if (Notification.permission === 'granted') {
        const notification = new Notification(title, {
          body,
          icon: '/assets/icon/favicon.png',
          data
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          this.handleNotificationClick(data);
        };
      }
    }
  }

  async removeNotificationToken(token: string) {
    try {
      if (!this.currentUserId || !this.currentOrganizationId || !this.currentDeviceId) {
        // Cannot remove token: missing required parameters - handled silently
        return;
      }

      // Remove the specific device from organization-scoped collection
      const deviceRef = doc(db, `organizations/${this.currentOrganizationId}/users/${this.currentUserId}/devices`, this.currentDeviceId);
      await deleteDoc(deviceRef);
      
    } catch (error) {
      throw error;
    }
  }

  async removeAllTokensForUser() {
    const maxRetries = 3;
    let retryCount = 0;
    
    while (retryCount < maxRetries) {
      try {
        if (!this.currentUserId || !this.currentOrganizationId) {
          return;
        }

        // Remove all devices for the user in the organization
        const devicesCollectionRef = collection(db, `organizations/${this.currentOrganizationId}/users/${this.currentUserId}/devices`);
        const devicesSnapshot = await getDocs(devicesCollectionRef);
        
        // Delete all device documents
        const deletePromises = devicesSnapshot.docs.map(deviceDoc => deleteDoc(deviceDoc.ref));
        await Promise.all(deletePromises);
        return; // Success, exit retry loop
        
      } catch (error) {
        retryCount++;
        
        if (retryCount >= maxRetries) {
          throw new Error(`Failed to remove all notification tokens after ${maxRetries} attempts: ${error.message}`);
        }
        
        // Wait before retry (exponential backoff)
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  async sendNotification(userId: string, title: string, body: string, data: any = {}) {
    try {
      if (!this.currentOrganizationId) {
        return;
      }

      // Get all devices for the user using organization-scoped collection
      const devicesCollectionRef = collection(db, `organizations/${this.currentOrganizationId}/users/${userId}/devices`);
      const devicesSnapshot = await getDocs(devicesCollectionRef);
      
      if (!devicesSnapshot.empty) {
        // Here you would typically call your backend to send the notification
        // The new structure makes it easier to get all device tokens
      }
    } catch (error) {
      // Error handled silently
    }
  }

  // New method to get all device tokens for a user
  async getUserDeviceTokens(userId: string): Promise<string[]> {
    try {
      if (!this.currentOrganizationId) {
        return [];
      }

      // Get all devices for the user using organization-scoped collection
      const devicesCollectionRef = collection(db, `organizations/${this.currentOrganizationId}/users/${userId}/devices`);
      const devicesSnapshot = await getDocs(devicesCollectionRef);
      const tokens: string[] = [];
      
      devicesSnapshot.forEach(doc => {
        const deviceData = doc.data();
        if (deviceData.fcmToken) {
          tokens.push(deviceData.fcmToken);
        }
      });
      
      return tokens;
    } catch (error) {
      return [];
    }
  }

  // New method to check if current device is registered
  async isCurrentDeviceRegistered(): Promise<boolean> {
    try {
      if (!this.currentUserId || !this.currentOrganizationId || !this.currentDeviceId) {
        return false;
      }

      const deviceRef = doc(db, `organizations/${this.currentOrganizationId}/users/${this.currentUserId}/devices`, this.currentDeviceId);
      const deviceDoc = await getDoc(deviceRef);
      
      return deviceDoc.exists();
    } catch (error) {
      return false;
    }
  }

  // Enhanced method to verify token synchronization
  async verifyTokenSync(): Promise<boolean> {
    try {
      if (!this.currentUserId || !this.currentOrganizationId || !this.currentDeviceId || !this.currentToken) {
        return false;
      }

      const deviceRef = doc(db, `organizations/${this.currentOrganizationId}/users/${this.currentUserId}/devices`, this.currentDeviceId);
      const deviceDoc = await getDoc(deviceRef);
      
      if (!deviceDoc.exists()) {
        return false;
      }

      const deviceData = deviceDoc.data();
      const isTokenMatch = deviceData.fcmToken === this.currentToken;
      
      return isTokenMatch;
    } catch (error) {
      return false;
    }
  }

  // Method to force token resync if needed
  async resyncToken(): Promise<boolean> {
    try {
      if (!this.currentToken) {
        return false;
      }

      await this.storeTokenInFirestore(this.currentToken);
      
      // Verify the resync was successful
      const isSynced = await this.verifyTokenSync();
      
      return isSynced;
    } catch (error) {
      return false;
    }
  }

  private isLikelyApnsToken(token: string): boolean {
    if (!token) {
      return false;
    }
    
    const apnsPattern = /^[0-9a-f]+$/i;
    return apnsPattern.test(token) && (token.length === 64 || token.length === 128);
  }
} 